import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Loader from '../../Loader';
import { WEB_API_BILLING } from '../../../assets/constants/strings';
import './style.scoped.scss';
import SettingsTitle from '../UIElements/SettingsTitle';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  ResponsiveContainer,
  Area,
} from 'recharts';
import { getApiUsage } from '../../../services/axios';
import ContactSalesModal from '../Home/Plans/ContactSalesModal';

const mapStateToProps = (state) => ({
  token: state.user.user ? state.user.user.token : null,
});

const connector = connect(mapStateToProps);

const Usage = ({ token }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [chartData, setChartData] = useState([]);
  const [totals, setTotals] = useState({ totalCalls: 0, totalCost: 0 });
  // Call-limit context (from the API). The limit is metered per PERIOD (this month, or this year for
  // an annual quota) and resets when the window rolls over.
  const [quota, setQuota] = useState({
    limit: 0,
    used: 0,
    remaining: 0,
    planName: null,
    interval: 'Monthly',
  });
  const [showRequest, setShowRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedDate, setSelectedDate] = useState({
    month: currentDate.getMonth(),
    year: currentDate.getFullYear(),
  });

  const calendarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch the caller's real usage for the displayed month. The .NET API scopes the
  // report to the authenticated client (via the JWT), so we only send year/month.
  useEffect(() => {
    let cancelled = false;

    const fetchUsage = async () => {
      if (!token) return;
      setIsLoading(true);

      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1; // API expects 1-12

      const res = await getApiUsage(token, year, month);

      if (cancelled) return;

      if (res && Array.isArray(res.days)) {
        setChartData(
          res.days.map((d) => ({ day: d.day.toString(), calls: d.count }))
        );
        setTotals({
          totalCalls: res.totalCalls || 0,
          totalCost: res.totalCost || 0,
        });
        setQuota({
          limit: res.callLimit || 0,
          used: res.used || 0,
          remaining: res.remaining || 0,
          planName: res.planName || null,
          interval: res.quotaInterval || 'Monthly',
        });
      } else {
        setChartData([]);
        setTotals({ totalCalls: 0, totalCost: 0 });
        setQuota({ limit: 0, used: 0, remaining: 0, planName: null, interval: 'Monthly' });
      }

      setIsLoading(false);
    };

    fetchUsage();

    return () => {
      cancelled = true;
    };
  }, [token, currentDate]);

  const togglePicker = () => {
    setIsPickerOpen(!isPickerOpen);
  };

  const changeMonth = (increment) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + increment);
    setCurrentDate(newDate);
    setSelectedDate({ ...selectedDate, month: currentDate.getMonth() });
  };

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const year = currentDate.getFullYear();
  const month = months[currentDate.getMonth()];

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let i = currentYear - 10; i <= currentYear + 10; i++) {
      yearOptions.push(i);
    }
    return yearOptions;
  };

  const monthPicker = (
    <div className='monthPicker'>
      <div className='monthHeader'>
        <span>Select a Month</span>
        <select
          value={selectedYear}
          onChange={(e) => handleYearChange(Number(e.target.value))}
        >
          {getYearOptions().map((yearOption) => (
            <option key={yearOption} value={yearOption}>
              {yearOption}
            </option>
          ))}
        </select>
      </div>
      <div className='monthGrid'>
        {months.map((monthName, index) => (
          <div
            key={monthName}
            className={`monthItem ${
              index === selectedDate.month ? 'selected' : ''
            }`}
            onClick={() => handleMonthClick(index)}
          >
            {monthName}
          </div>
        ))}
      </div>
    </div>
  );

  const handleMonthClick = (selectedMonthIndex) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(selectedMonthIndex);
    setCurrentDate(newDate);
    setIsPickerOpen(false);
    setSelectedDate({
      month: selectedMonthIndex,
      year: selectedYear,
    });
  };

  const handleYearChange = (newYear) => {
    setSelectedYear(newYear);
    const newDate = new Date(currentDate);
    newDate.setFullYear(newYear);
    setCurrentDate(newDate);
    setSelectedDate({
      month: selectedDate.month,
      year: newYear,
    });
  };

  const quotaPct =
    quota.limit > 0
      ? Math.min(100, Math.round((quota.used / quota.limit) * 100))
      : 0;
  const atLimit = quota.limit > 0 && quota.remaining === 0;
  const nearLimit =
    quota.limit > 0 &&
    !atLimit &&
    quota.remaining <= Math.max(1, Math.round(quota.limit * 0.2));
  const barColor = atLimit ? '#dc3545' : nearLimit ? '#e0a800' : '#00a587';
  const quotaPeriodLong =
    quota.interval === 'Annual' ? 'this year' : quota.interval === 'Monthly' ? 'this month' : '';

  return (
    <div className='transactions-frame' id='transactions-frame'>
      <SettingsTitle title={WEB_API_BILLING.USAGE.TITLE} />

      {quota.limit > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.25rem',
            flexWrap: 'wrap',
            border: '1px solid #e6e9ef',
            borderLeft: `4px solid ${barColor}`,
            borderRadius: '10px',
            padding: '1rem 1.25rem',
            margin: '0 0 1.25rem',
            background: '#fff',
          }}
        >
          <div style={{ flex: '1 1 320px', minWidth: '260px' }}>
            <div style={{ fontWeight: 600, marginBottom: '.4rem' }}>
              {quota.planName ? `${quota.planName} — ` : ''}
              {quota.used.toLocaleString()} of{' '}
              {quota.limit.toLocaleString()} API calls used
              {quotaPeriodLong ? ` ${quotaPeriodLong}` : ''}
              {atLimit
                ? ' — limit reached'
                : ` — ${quota.remaining.toLocaleString()} remaining`}
            </div>
            <div
              style={{
                height: '8px',
                borderRadius: '999px',
                background: '#eef1f6',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${quotaPct}%`,
                  height: '100%',
                  background: barColor,
                  transition: 'width .3s ease',
                }}
              />
            </div>
          </div>
          <button
            className='wf-button'
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => setShowRequest(true)}
          >
            Request more calls
          </button>
        </div>
      )}

      <div className='graphContainer'>
        <p>
          Below you'll find a summary of cumulative API usage for your
          organisation.
        </p>
        <div className='calendaPicker' ref={calendarRef}>
          <div className='calendarHeader'>
            <button className='arrowButton' onClick={() => changeMonth(-1)}>
              <HiChevronLeft />
            </button>
            <span onClick={togglePicker}>
              <b>
                {month} {year}
              </b>
            </span>
            <button className='arrowButton' onClick={() => changeMonth(1)}>
              <HiChevronRight />
            </button>
          </div>
          {isPickerOpen && monthPicker}
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <ResponsiveContainer width='100%' height={400}>
            <AreaChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis dataKey='day' />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Area
                type='monotone'
                dataKey='calls'
                name='API calls'
                stroke='#8884d8'
                fill='#8884d8'
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      <h4>
        <b>Total API calls this month: {totals.totalCalls.toLocaleString()}</b>
      </h4>
      {totals.totalCost > 0 && (
        <h4>
          <b>Estimated cost this month: ${totals.totalCost.toFixed(2)}</b>
        </h4>
      )}

      <ContactSalesModal
        show={showRequest}
        handleClose={() => setShowRequest(false)}
        title='Request more API calls'
        prefix={
          quota.planName
            ? `Request to raise the API call allowance (currently on the ${quota.planName} plan — ${quota.limit.toLocaleString()} calls ${quotaPeriodLong || 'total'}, ${quota.used.toLocaleString()} used).\n\n`
            : 'Request to raise the API call allowance.\n\n'
        }
        defaultMessage="We'd like more API calls added to our account."
      />
    </div>
  );
};

export default connector(Usage);
